import React from "react";
import { View, Text, StyleSheet, TouchableOpacity, Image } from "react-native";
import { useTranslation } from "react-i18next";
import { useNavigation } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { RootStackParamList } from "../../navigator";
import SvaraModal from "./SvaraModal";
import { BtnCloseModal } from "../../../assets";
import { useDevice } from "../../../utils/hooks/useDevice";
import crownIcon from "../../../assets/main/vipButton/crown.svg";
import vipButtonEn from "../../../assets/main/vipButton/vip_icon_en.svg";
import vipButtonBg from "../../../assets/main/vipButton/vip_icon_bg.svg";
import vipButtonRu from "../../../assets/main/vipButton/vip_icon_ru.svg";
import vipButtonFr from "../../../assets/main/vipButton/vip_icon_fr.svg";
import starIcon from "../../../assets/main/vipButton/star_yellow.svg";
import barIcon from "../../../assets/main/vipButton/bar.png";

interface IProps {
  onClose: () => void;
}

type Nav = NativeStackNavigationProp<RootStackParamList>;

const BecomeVipModal: React.FC<IProps> = ({ onClose }) => {
  const { t, i18n } = useTranslation();
  const { navigate } = useNavigation<Nav>();
  const { isDesktop, isXs } = useDevice();

  const currentLanguage = i18n.language || "bg";
  const setImage = (lang: string) => {
    switch (lang) {
      case "en":
        return vipButtonEn;
      case "bg":
        return vipButtonBg;
      case "ru":
        return vipButtonRu;
      case "fr":
        return vipButtonFr;
      default:
        return vipButtonBg;
    }
  };
  const vipButtonImage = setImage(currentLanguage);

  const handleBecomeVip = () => {
    onClose();
    navigate("BuyPackages", { activeTab: "vip" });
  };

  const parseAndRenderText = (htmlString: string) => {
    if (htmlString.includes("<img")) {
      const parts = htmlString.split(/<img[^>]*>/);
      return (
        <View style={styles.featureWithImage}>
          <Text style={styles.featureText}>{parseTextWithSpan(parts[0])}</Text>
          <Image source={barIcon} style={styles.barIcon} resizeMode="contain" />
        </View>
      );
    }

    return (
      <Text style={styles.featureText}>{parseTextWithSpan(htmlString)}</Text>
    );
  };

  const parseTextWithSpan = (text: string) => {
    const spanRegex = /<span>(.*?)<\/span>/g;
    const parts: (string | JSX.Element)[] = [];
    let lastIndex = 0;
    let match;

    while ((match = spanRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      parts.push(
        <Text key={match.index} style={styles.highlight}>
          {match[1]}
        </Text>
      );
      lastIndex = spanRegex.lastIndex;
    }

    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    return parts.length > 0 ? parts : text;
  };

  return (
    <SvaraModal
      visible
      onClose={onClose}
      closeIcon={BtnCloseModal}
      bigCloseButton
      bigCloseBtnStyles={{
        width: 42,
        height: 42,
        ...(isXs && { transform: [{ translateX: 11 }, { translateY: -10 }] }),
      }}
    >
      <View style={[styles.container, { width: isDesktop ? 600 : 340 }]}>
        <View style={styles.content}>
          {/* Crown Icon */}
          <View style={styles.crownContainer}>
            <Image
              source={crownIcon}
              style={styles.crown}
              resizeMode="contain"
            />
          </View>

          {/* VIP Features Box */}
          <View style={styles.featuresBox}>
            <View style={styles.featuresHeader}>
              <Text style={styles.featuresTitle}>
                {t("T_SITE_SHOP_VIP_WHAT")}
              </Text>
            </View>

            <View style={styles.featuresList}>
              {[1, 2, 3, 4, 5].map((index) => (
                <View key={index} style={styles.featureItem}>
                  <Image
                    source={starIcon}
                    style={styles.starIcon}
                    resizeMode="contain"
                  />
                  {parseAndRenderText(t(`T_SITE_SHOP_VIP_WHAT_${index}`))}
                </View>
              ))}
            </View>
          </View>

          {/* Become VIP Button */}
          <TouchableOpacity
            style={styles.becomeVipButton}
            onPress={handleBecomeVip}
            activeOpacity={0.8}
          >
            <Image
              source={vipButtonImage}
              style={styles.vipButtonImage}
              resizeMode="contain"
            />
          </TouchableOpacity>
        </View>
      </View>
    </SvaraModal>
  );
};

export default BecomeVipModal;

const styles = StyleSheet.create({
  container: {
    maxHeight: "85%",
  },
  content: {
    alignItems: "center",
    paddingHorizontal: 20,
    paddingVertical: 20,
  },
  crownContainer: {
    width: 100,
    height: 100,
    marginBottom: 10,
  },
  crown: {
    width: "100%",
    height: "100%",
  },
  featuresBox: {
    width: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.3)",
    borderRadius: 8,
    overflow: "hidden",
    marginBottom: 40,
  },
  featuresHeader: {
    backgroundColor: "#0089d7",
    paddingVertical: 10,
  },
  featuresTitle: {
    color: "#fff",
    fontSize: 18,
    fontWeight: "700",
    textAlign: "center",
  },
  featuresList: {
    paddingHorizontal: 12,
    paddingVertical: 12,
  },
  featureItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 8,
  },
  starIcon: {
    width: 16,
    height: 16,
    marginRight: 8,
    marginTop: 2,
  },
  featureText: {
    flex: 1,
    color: "#fff",
    fontSize: 18,
    lineHeight: 26,
  },
  highlight: {
    color: "#ffa100",
    fontWeight: "bold",
  },
  featureWithImage: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
  },
  barIcon: {
    width: 60,
    height: 60, 
    marginLeft: 4,
  },
  becomeVipButton: {
    width: "60%",
    aspectRatio: 2.5,
  },
  vipButtonImage: {
    width: "100%",
    height: "100%",
  },
});
